export class NFTRedeemCalculator {
    private static readonly EPOCH_BITS = 16;
    private static readonly SIZE_BITS = 8;
    private static readonly POWER_N_BITS = 16;
    private static readonly CRYPTO_DECIMALS = 18;
    private static readonly PERCENTAGE_DECIMALS = 10000;

    private static base64ToHex(base64String: string): string {
        try {
            // Remove any whitespace and padding
            const cleanBase64 = base64String.replace(/\s/g, '').replace(/=+$/, '');
            
            // Add padding if necessary
            const padLength = cleanBase64.length % 4;
            const paddedBase64 = padLength > 0 
                ? cleanBase64 + '='.repeat(4 - padLength)
                : cleanBase64;

            const raw = atob(paddedBase64);
            let result = '';
            for (let i = 0; i < raw.length; i++) {
                const hex = raw.charCodeAt(i).toString(16).padStart(2, '0');
                result += hex;
            }
            return result;
        } catch (error) {
            console.error('Error decoding base64:', error);
            console.log('Problematic base64 string:', base64String);
            return '';
        }
    }

    private static hexToInt(hex: string): number {
        return parseInt(hex, 16);
    }

    private static convertFromCryptoUnits(value: number): number {
        return value / Math.pow(10, this.CRYPTO_DECIMALS);
    }

    static calculateRedeemValue({
        currentEpoch,
        startEpoch,
        finalEpoch,
        minPercentage,
        maxValue,
        n = 1
    }) {
        console.log('Calculating redeem value:', {
            currentEpoch,
            startEpoch,
            finalEpoch,
            minPercentage,
            maxValue,
            n
        });

        if (currentEpoch <= startEpoch) {
            const actualValue = this.convertFromCryptoUnits(maxValue) * minPercentage / 100;
            return {
                percentage: minPercentage,
                actualValue
            };
        }

        if (currentEpoch >= finalEpoch) {
            const actualValue = this.convertFromCryptoUnits(maxValue);
            return {
                percentage: 100,
                actualValue
            };
        }

        // Calculate x/xmax
        const x = (currentEpoch - startEpoch);
        const xmax = (finalEpoch - startEpoch);
        const ratio = x / xmax;

        // y = ymin + (100-ymin) * (x/xmax)^n
        const percentage = minPercentage + (100 - minPercentage) * Math.pow(ratio, n);
        const actualValue = this.convertFromCryptoUnits(maxValue) * percentage / 100;

        return {
            percentage,
            actualValue
        };
    }

    static parseAttributes(base64Attributes: string): {
        startEpoch: number;
        finalEpoch: number;
        minPercentage: number;
        maxValue: number;
        powerN: number;
    } {
        const hex = this.base64ToHex(base64Attributes);

        // Parse epochs
        const startEpoch = this.hexToInt(hex.slice(0, this.EPOCH_BITS));
        const finalEpoch = this.hexToInt(hex.slice(this.EPOCH_BITS, this.EPOCH_BITS * 2));

        // Get value size in bytes
        const valueSize = this.hexToInt(hex.slice(this.EPOCH_BITS * 2, this.EPOCH_BITS * 2 + this.SIZE_BITS));

        // Starting index after epochs and size
        const valueStartIndex = this.EPOCH_BITS * 2 + this.SIZE_BITS;

        // Get the TADA value
        const maxValue = this.hexToInt(hex.slice(valueStartIndex, valueStartIndex + valueSize * 2));

        // Get power N value
        const powerN = this.hexToInt(hex.slice(
            valueStartIndex + valueSize * 2,
            valueStartIndex + valueSize * 2 + this.POWER_N_BITS
        ));

        // Get min percentage (last 5 chars for more precision)
        const minPercentageRaw = this.hexToInt(hex.slice(-5));
        const minPercentage = minPercentageRaw / this.PERCENTAGE_DECIMALS;

        return {
            startEpoch,
            finalEpoch,
            minPercentage,
            maxValue,
            powerN,
        };
    }
} 